import React from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../contexts/ToastContext';

const VARIANT_STYLES: Record<string, string> = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800'
};

const ICON_STYLES: Record<string, { bg: string; icon: string }> = {
    success: { 
        bg: 'bg-emerald-500', 
        icon: 'M5 13l4 4L19 7' 
    },
    error: { 
        bg: 'bg-red-500', 
        icon: 'M6 18L18 6M6 6l12 12' 
    },
    info: { 
        bg: 'bg-blue-500', 
        icon: 'M13 16h-1v-4h-1m1-4h.01' 
    },
    warning: { 
        bg: 'bg-amber-500', 
        icon: 'M12 9v2m0 4h.01' 
    }
};

export const ToastContainer: React.FC = () => {
    const { toasts, dismiss } = useToast();
    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className="fixed top-20 right-4 z-[99999] flex flex-col space-y-3 max-w-sm pointer-events-none" id="toast-container">
            {toasts.map(t => {
                const variantStyle = VARIANT_STYLES[t.variant] || VARIANT_STYLES.info;
                const iconStyle = ICON_STYLES[t.variant] || ICON_STYLES.info;
                
                return (
                    <div
                        key={t.id}
                        role="status"
                        className={`
                            relative flex items-start p-4 rounded-lg border shadow-lg backdrop-blur-sm pointer-events-auto
                            transform transition-all duration-300 ease-out
                            animate-slide-in
                            ${variantStyle}
                        `}
                    >
                        {/* Icon */}
                        {!t.hideIcon && (
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full ${iconStyle.bg} flex items-center justify-center mr-3`}>
                                <svg 
                                    className="w-4 h-4 text-white" 
                                    stroke="currentColor" 
                                    strokeWidth={2} 
                                    fill="none" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    viewBox="0 0 24 24"
                                >
                                    <path d={iconStyle.icon} />
                                </svg>
                            </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            {t.title && (
                                <h4 className="text-sm font-semibold mb-1">
                                    {t.title}
                                </h4>
                            )}
                            <p className="text-sm leading-relaxed">
                                {t.message}
                            </p>
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={() => dismiss(t.id)}
                            aria-label="Dismiss notification"
                            className="
                                flex-shrink-0 ml-3 w-6 h-6 rounded-md 
                                flex items-center justify-center
                                hover:bg-black/5 transition-colors duration-200
                                text-gray-400 hover:text-gray-600
                            "
                        >
                            <svg 
                                className="w-4 h-4" 
                                stroke="currentColor" 
                                strokeWidth={2} 
                                fill="none" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                viewBox="0 0 24 24"
                            >
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                );
            })}
        </div>,
        document.body
    );
};

export default ToastContainer;
